<script setup lang="ts">
import {
  createOneChatSceneCharacter,
  deleteOneChatSceneCharacter,
} from "@/apis/chatScene";
import { ContentRatingEnum } from "@/enums";
import { useChatScene } from "@/stores/chatScene";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { ChatScene } from "@/types/chat";
import { MediaUploadFileItem } from "@/types/component";
import { Mate } from "@/types/entity";
import { useTextareaAutosize } from "@vueuse/core";
import { showConfirmDialog, showLoadingToast } from "vant";
import { computed, onMounted, PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps({
  targetScene: {
    type: Object as PropType<ChatScene>,
    required: true,
  },
});
const emit = defineEmits(["save"]);
const { t } = useI18n();
const chatSceneStore = useChatScene();
const user = useGlobalUser();
const mateStore = useMate();
const { textarea, input } = useTextareaAutosize();

watch(input, (val) => (props.targetScene.intro = val));

const contentRatingOptions: {
  label: string;
  value?: ContentRatingEnum;
  icon?: string;
}[] = [
  {
    label: "nsfw.nsfw",
    value: ContentRatingEnum.NSFW,
    icon: "iconify mdi--shield-warning-outline",
  },
  {
    label: "nsfw.sfw",
    value: ContentRatingEnum.SFW,
    icon: "iconify mingcute--safety-certificate-line",
  },
];

const selectedTagList = computed({
  get: () => props.targetScene.tag!.split(","),
  set: (val: string[]) => {
    props.targetScene.tag = val.join(",");
  },
});

const showMediaSelectPopup = ref(false);
const mediaList = ref<MediaUploadFileItem[]>([]);
const onUpload = (list: MediaUploadFileItem[]) => {
  mediaList.value = list;
  props.targetScene.media = list[0] as Media;
  props.targetScene.mediaId = list[0].mediaId;
  emit("save", "mediaId");
};

const showCharacterSelectPopup = ref(false);

const removeCharacter = (characterId: number) => {
  showConfirmDialog({
    message: t("Message.deleteCharacterTips"),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  }).then(async () => {
    props.targetScene.characterzz = props.targetScene.characterzz?.filter(
      (character) => character.id !== characterId
    );
    chatSceneStore.withSaving(async () => {
      await deleteOneChatSceneCharacter(characterId);
    });
  });
};

const onSelectCharacter = async (mate: Mate) => {
  const loading = showLoadingToast({
    message: t("Message.saving"),
    duration: 0,
  });
  chatSceneStore.withSaving(async () => {
    const { item } = await createOneChatSceneCharacter(
      props.targetScene.id!,
      mate.id!
    );
    props.targetScene.characterzz?.push({ ...item, mate });
    showCharacterSelectPopup.value = false;
    loading.close();
  });
};

const updateContentRating = (val?: number | string) => {
  props.targetScene.isNsfw = val
    ? ContentRatingEnum.NSFW
    : ContentRatingEnum.SFW;
  emit("save", "isNsfw");
};

const updateTag = (val: string[]) => {
  selectedTagList.value = val;
  props.targetScene.tag = val.join(",");
  emit("save", "tag");
};

onMounted(() => {
  input.value = props.targetScene.intro ?? "";
});
</script>

<template>
  <div class="pb-4">
    <VanCellGroup inset>
      <VanField
        v-model="targetScene.name"
        :label="$t('Chats.chatScene.edit.name')"
        :name="$t('User.mate.basicInfo.name')"
        :placeholder="$t('Chats.chatScene.edit.namePlaceholder')"
        :rules="[
          {
            required: true,
            message: $t('Chats.chatScene.edit.namePlaceholder'),
          },
        ]"
        @blur="$emit('save', 'name')"
        required
      />
      <!--              <VanField-->
      <!--                :label="$t('Chats.chatScene.edit.roundsLimit')"-->
      <!--                :model-value="-->
      <!--                  targetScene.maxRounds != undefined-->
      <!--                    ? String(targetScene.maxRounds)-->
      <!--                    : ''-->
      <!--                "-->
      <!--                :name="$t('User.mate.basicInfo.roundsLimit')"-->
      <!--                :placeholder="$t('Chats.chatScene.edit.roundsLimitPlaceholder')"-->
      <!--                type="digit"-->
      <!--                @update:model-value="targetScene.maxRounds = $event"-->
      <!--              />-->
      <div class="relative flex bg-base-200 px-3 pt-2 pb-4">
        <div class="form-control w-full">
          <div class="flex w-full items-center justify-between">
            <div class="label pt-0">
              <div class="flex items-center label-text required-field">
                {{ $t("Chats.chatScene.edit.shortDesc") }}
              </div>
            </div>
          </div>
          <textarea
            ref="textarea"
            v-model="input"
            :placeholder="$t('Chats.chatScene.edit.shortDescPlaceholder')"
            class="input input-bordered w-full rounded textarea min-h-[3rem] text-xs join-item text-base-content overflow-hidden"
            @blur="$emit('save', 'intro', input)"
          />
        </div>
      </div>
      <VanField
        v-model="targetScene.name"
        :label="$t('Buttons.setDefault')"
        :name="$t('Buttons.setDefault')"
      >
        <template #input>
          <VanCheckbox
            v-model="targetScene.setAsDefault"
            @change="$emit('save', 'setAsDefault')"
          />
        </template>
      </VanField>
    </VanCellGroup>
    <div class="p-4 pt-2 bg-base-200 m-4 rounded-lg flex flex-col gap-2">
      <div v-if="!user.isHideNSFW" class="flex justify-between items-center">
        <span class="flex-1">
          {{ $t("nsfw.contentRating") }}
        </span>
        <span class="flex justify-end relative z-10 flex-1">
          <SingleSelect
            :selected="targetScene.isNsfw"
            :options="contentRatingOptions"
            @update:selected="updateContentRating"
          />
        </span>
      </div>
      <div
        v-if="!user.isHideNSFW"
        class="border border-t h-0 w-full /40 my-[1px]"
      />
      <div class="flex justify-between">
        <span>{{ $t("User.mate.basicInfo.tag") }}</span>
        <span class="text-base-content/50">
          {{ $t("User.mate.basicInfo.rules.tag") }}
        </span>
      </div>
      <TagSelector :tag="selectedTagList" @update:tag="updateTag" />
    </div>
    <div class="p-4 bg-base-200 m-4 rounded-lg flex flex-col gap-2">
      <div class="flex justify-between items-center">
        <span class="required-field">
          {{ $t("Chats.chatScene.edit.coverImage") }}
        </span>
        <span class="text-base-content/50">
          {{ $t("Chats.chatScene.edit.coverImageTip") }}
        </span>
      </div>
      <div>
        <button
          v-if="!targetScene.media"
          class="btn btn-sm btn-neutral"
          @click="showMediaSelectPopup = true"
        >
          <i class="iconify mingcute--add-line" />
          {{ $t("Buttons.add") }}
        </button>
        <div v-else class="flex items-end gap-2">
          <div class="relative aspect-[3/4] rounded-lg overflow-hidden w-48">
            <ImageCard
              :media="targetScene.media"
              class="w-full h-full"
              @click="showMediaSelectPopup = true"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="p-4 bg-base-200 m-4 rounded-lg flex flex-col gap-2">
      <div class="flex justify-between items-center gap-2">
        <span class="required-field">
          {{ $t("Chats.chatScene.edit.character") }}
        </span>
        <span class="text-base-content/50">
          {{ $t("Chats.chatScene.edit.characterDesc") }}
        </span>
      </div>

      <div class="flex gap-2 flex-wrap justify-start">
        <div
          class="flex flex-col items-center gap-0.5 relative"
          v-for="character in targetScene.characterzz"
          :key="character?.id"
        >
          <div
            v-if="character.role === 2"
            class="flex flex-col items-center gap-0.5"
          >
            <UserAvatar class="w-16" />
            <div class="text-xs max-w-16">{{ $t(`game.player`) }}</div>
          </div>
          <template v-else>
            <i
              class="iconfont icon-close absolute top-[-8px] right-[-8px] z-10 text-xs bg-base-100 rounded-full cursor-pointer"
              v-if="character.mateId !== mateStore.currentMate?.id"
              @click="removeCharacter(character?.id!)"
            />
            <UserAvatar class="w-16" :avatar-url="character?.mate?.mediaUrl" />
            <div class="text-xs max-w-16">
              {{ character?.mate?.name }}
            </div>
          </template>
        </div>
      </div>
      <button
        class="btn btn-sm btn-neutral"
        @click="showCharacterSelectPopup = true"
      >
        {{ $t("Buttons.add") }}
      </button>
    </div>
  </div>

  <MediaSelectPopup
    v-model:show="showMediaSelectPopup"
    :allow-nsfw="true"
    :allowCropOutside="false"
    :max-count="1"
    :multiple="false"
    :upload-type="7"
    mediaType="img"
    replace
    @onMediaSelected="onUpload"
  />

  <ModalSelectCharacter
    v-model:show="showCharacterSelectPopup"
    :show-tab="true"
    :selected-ids="[
      ...targetScene.characterzz?.map((character) => character?.mateId!),
      mateStore.currentMate?.id,
    ]"
    @select="onSelectCharacter"
  />
</template>
